import React, { useEffect } from 'react';
import { Mic, Radio } from 'lucide-react';

const RING_R = 56;
const RING_LEN = 2 * Math.PI * RING_R;

export const PushToTalkButton = ({ onStart, onStop, isRecording, elapsed, maxSeconds, disabled, sending, permission }) => {
  useEffect(() => {
    if (!isRecording) return undefined;
    const release = () => onStop();
    window.addEventListener('blur', release);
    window.addEventListener('pointerup', release);
    return () => {
      window.removeEventListener('blur', release);
      window.removeEventListener('pointerup', release);
    };
  }, [isRecording, onStop]);

  const handleDown = (e) => {
    if (disabled || e.button > 0) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not supported */ }
    onStart();
  };
  const handleUp = (e) => { e.preventDefault(); onStop(); };

  const label = isRecording
    ? `Talking… ${Math.round(elapsed)}s / ${maxSeconds}s`
    : sending ? 'Sending voice…' : permission === 'denied' ? 'Microphone blocked' : 'Hold to talk';

  return (
    <div className="ptt-wrap" data-testid="ptt-wrap">
      <button
        type="button"
        className={`ptt-button ${isRecording ? 'active' : ''}`}
        disabled={disabled}
        onPointerDown={handleDown}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => { if (e.key === ' ' && !e.repeat && !disabled) { e.preventDefault(); onStart(); } }}
        onKeyUp={(e) => { if (e.key === ' ') { e.preventDefault(); onStop(); } }}
        aria-pressed={isRecording}
        aria-label="Hold to talk"
        data-testid="ptt-button"
      >
        <svg className="ptt-ring" viewBox="0 0 120 120" aria-hidden="true">
          <circle
            cx="60" cy="60" r={RING_R}
            strokeDasharray={RING_LEN}
            strokeDashoffset={RING_LEN * (1 - Math.min(1, elapsed / maxSeconds))}
            style={{ opacity: isRecording ? 1 : 0 }}
          />
        </svg>
        {isRecording ? <Mic size={36} /> : <Radio size={36} />}
      </button>
      <div className={`ptt-status ${isRecording ? 'talking' : ''}`} data-testid="ptt-status">{label}</div>
      {permission === 'denied' && (
        <div className="ptt-help" data-testid="ptt-permission-help">
          Allow microphone access for this site in your browser settings, then try again.
        </div>
      )}
    </div>
  );
};
